// The LLM-facing s-expression DSL reference.
//
// `<ExprDslReference>` is the canonical, self-contained single source of
// truth: one syntax primer, one op table per dispatch scope (every op
// emitted exactly once, deduped by name across the whole scope graph),
// and one enum-domain glossary. It is mounted ONCE in the system prompt
// — tools do not re-embed the grammar; they rely on this one
// block. Rendering is composed entirely from
// the dedicated components in `components.tsx` so every doc surface is
// byte-identical.

import { Fragment, type ReactNode } from 'react';

import { canonicalDslRoots, walkOpMeta } from '../op-surface.ts';
import { dashboardEnumDomains, enumDomainNameOf } from '../ops/dashboard/enums.ts';
import { EnumDomains, OpDocTable } from './components.tsx';
import { entryOf, type OpDocScopeView } from './op-entry.ts';

export type { EnumDomainDoc } from './types.ts';

/**
 * The canonical DSL reference. Self-contained — no props. Mounted once
 * in the system prompt as an `<Instructions>` block; tools do not
 * re-embed the grammar — this is the only copy.
 */
export function ExprDslReference(): ReactNode {
  const { views } = walkOpMeta(canonicalDslRoots).reduce<{
    readonly seen: ReadonlySet<string>;
    readonly views: readonly OpDocScopeView[];
  }>(
    (acc, { scope, entries }) => {
      const docEntries = entries
        .filter(({ meta }) => meta?.docHidden !== true)
        .map(({ op, meta }) => entryOf(op, meta, enumDomainNameOf));
      // Terse-grouped entries render only their name in a group's Ops list, so
      // they can never collide on a signature — exempt them from cross-scope
      // name dedup (and don't let them claim a name for later scopes either).
      const fresh = docEntries.filter(
        (e, i) =>
          (e.terseGroup !== undefined || !acc.seen.has(e.name)) && docEntries.findIndex((x) => x.name === e.name) === i,
      );
      if (fresh.length === 0) {
        return acc;
      }
      const claimable = fresh.filter((e) => e.terseGroup === undefined).map((e) => e.name);
      return {
        seen: new Set([...acc.seen, ...claimable]),
        views: [...acc.views, { name: scope.name, description: scope.description, entries: fresh }],
      };
    },
    { seen: new Set<string>(), views: [] },
  );

  return (
    <>
      <h1>S-expression DSL reference</h1>
      <p>
        Programs are nested s-expression calls. The sections below give the surface syntax, then one op table per
        dispatch scope — the blurb under each scope heading says where its ops are reachable. Each op lists its call
        signature, the meaning of every keyword argument, and a worked example. Enum-typed kwargs name a domain (e.g.{' '}
        <code>Color</code>); the legal values are in the final glossary.
      </p>
      <h2>Syntax</h2>
      <p>
        Every program is a tree of nested calls in s-expression form:{' '}
        <code>(op positional… :kwarg value … child child …)</code>. Positional arguments come first, then keyword
        arguments (<code>:name value</code> pairs), then any child forms.
      </p>
      <p>Argument values can be:</p>
      <ul>
        <li>
          bare symbols — e.g. <code>kpi</code>, <code>compact</code>, <code>sales</code>
        </li>
        <li>
          quoted strings — e.g. <code>"Revenue"</code> (use quotes when the value has spaces or punctuation)
        </li>
        <li>
          numbers — e.g. <code>4</code>, <code>1.5</code>, <code>-10</code>
        </li>
        <li>
          booleans — <code>true</code> / <code>false</code>
        </li>
        <li>
          vectors in square brackets — e.g. <code>[4 2]</code>, <code>[:name [:createdTime :desc]]</code>
        </li>
        <li>
          nested forms — another <code>(op …)</code> call
        </li>
      </ul>
      <p>
        Null propagation: every scalar op returns null when any argument is null or missing, unless its entry says
        otherwise — string predicates (<code>contains</code>, <code>starts-with</code>, <code>ends-with</code>) return
        false, <code>index-of</code> returns -1, and <code>concat</code> treats null as <code>""</code>.
      </p>
      <p>
        In the signatures below, <code>&lt;col&gt;</code> is a column name written as a keyword (<code>:rate</code>) or
        a quoted string; <code>ColRef</code> is an explicit <code>(col ds name)</code> reference.
      </p>
      <h2>Column references</h2>
      <p>
        Reference a column of a dataset with the polymorphic <code>(col …)</code> op:
      </p>
      <ul>
        <li>
          <code>(col &lt;binding&gt; &lt;column&gt;)</code> — by bound dataset name (the usual case; the name comes from
          a prior tool's <code>outputDataset</code>)
        </li>
        <li>
          <code>(col &lt;dataset-expr&gt; &lt;column&gt;)</code> — by an inline expression that evaluates to a Dataset
        </li>
        <li>
          <code>(col &lt;binding&gt; &lt;column&gt; &lt;path&gt; …)</code> — extra path segments walk into nested struct
          columns
        </li>
      </ul>
      <p>
        A <code>TimeSeries</code> ref column is a <em>nested dataset of raw datapoints</em>, with inner columns{' '}
        <code>timestamp</code>, <code>value</code>, <code>isString</code>, and <code>unit</code>. There are two ways to
        consume it: <code>(unnest ds :tsCol :pick [:timestamp :value] :as [:ts :val])</code> flattens it to raw,
        unaggregated per-sample rows (use this for raw datapoints);{' '}
        <code>(time-bucket ds [:tsCol] "1d" :window ["&lt;start&gt;" "&lt;end&gt;"] :agg [:mean])</code> aggregates it
        into time buckets. <code>time-bucket</code> always aggregates — it cannot return raw samples.
      </p>
      <p>
        Every query result carries scalar <code>externalId</code> and <code>space</code> identity columns plus its
        declared view properties. To filter one query by ids another query produced, bind the first to a dataset and
        reference its column inside the second's <code>:filter</code> lambda — e.g.{' '}
        <code>#(in (:externalId %) (col other-ds externalId))</code>. To filter by a direct relation, target the
        relation's <code>externalId</code> subpath:{' '}
        <code>#(in (col % relCol externalId) (col other-ds externalId))</code>. Negate with <code>#(not (in …))</code>.
        A cross-dataset set reference matches by <code>externalId</code> alone, so both queries must resolve to the same
        space, and the referenced column must yield a bounded, non-empty value set — narrow the upstream query if it is
        too large or empty (an empty referenced set is a hard error).
      </p>
      <p>
        In tile and sub-form signatures a slot typed <code>ColRef</code> always means exactly one such{' '}
        <code>(col …)</code> reference.
      </p>
      {/*   <h3>Relation lists & array&lt;struct&gt;</h3> */}
      {/*   <p> */}
      {/*     A relation-list column (<code>array&lt;reference&lt;View&gt;&gt;</code>, e.g.{' '} */}
      {/*     <code>ClassifiedTimeSeries.assets</code>) and any <code>array&lt;struct&gt;</code> column support field */}
      {/*     projection: <code>(col % assets name)</code> reads the <code>name</code> field from every element and returns an{' '} */}
      {/*     <code>array&lt;string&gt;</code>. Paths walk struct fields and a relation's identity axis (<code>space</code>,{' '} */}
      {/*     <code>externalId</code>) and NEVER take an integer index. Use <code>unroll</code> to get one row per element, or{' '} */}
      {/*     <code>str-join</code> to render a whole list as a single label. */}
      {/*   </p> */}
      {/*   <h3>Relation-list cookbook</h3> */}
      {/*   <p> */}
      {/*     Derive a scalar label first (project the relation list, then <code>str-join</code> it), then group by that */}
      {/*     scalar. */}
      {/*   </p> */}
      {/*   <p> */}
      {/*     Rank group companies by deviation: */}
      {/*     <code>{`(-> ei-series-by-gc */}
      {/* (filter #(in (:valueRole %) ["ACTUAL" "TARGET"])) */}
      {/* (time-bucket [:datapoints] "1d" :window [start end] :agg [:last]) */}
      {/* (unnest [:datapoints] :as [:day :value]) */}
      {/* (filter #(is-not-null (:value %))) (sort [:day :desc]) */}
      {/* (derive :gc_name #(str-join (col % assets name) " / ")) */}
      {/* (aggregate :group-by [:gc_name :valueRole] :pairs {:latest (first :value)}) */}
      {/* (pivot :rows [:gc_name] :cols [:valueRole] :values (first :latest)) */}
      {/* (derive :deviation_pct #(if (or (is-null (:TARGET %)) (= (:TARGET %) 0)) null */}
      {/*                           (* (/ (- (:ACTUAL %) (:TARGET %)) (:TARGET %)) 100))) */}
      {/* (sort [:deviation_pct :desc]))`}</code> */}
      {/*   </p> */}
      {/*   <p> */}
      {/*     Actual/target columns per group-company name, summed: */}
      {/*     <code>{`(-> ei-series-by-gc */}
      {/* (filter #(in (:valueRole %) ["ACTUAL" "TARGET"])) */}
      {/* (time-bucket [:datapoints] "1d" :window [start end] :agg [:last]) */}
      {/* (unnest [:datapoints] :as [:day :value]) (filter #(is-not-null (:value %))) */}
      {/* (derive :gc_name #(str-join (col % assets name) " / ")) */}
      {/* (aggregate :group-by [:gc_name :valueRole] :pairs {:total (sum :value)}) */}
      {/* (pivot :rows [:gc_name] :cols [:valueRole] :values (sum :total)))`}</code> */}
      {/*   </p> */}
      {views.map((v) => (
        <Fragment key={v.name}>
          <OpDocTable description={v.description} entries={v.entries} name={v.name} />
        </Fragment>
      ))}
      <EnumDomains domains={dashboardEnumDomains()} />
    </>
  );
}
